/**
 * The one thing listening for colleague calls, mounted for the whole session.
 *
 * `incoming-ring.ts` explains WHY the criteria is category-wide and what that
 * costs. This is the wiring: parse, decide, and hand the decision to
 * `ring-store`, which owns the ring's lifecycle. Nothing here starts or stops a
 * ring by mounting or unmounting.
 *
 * It renders nothing. The ring is drawn by the overlay stack.
 */

import type { Bundle } from "@medplum/fhirtypes";
import { useMedplum, useSubscription } from "@medplum/react-hooks";
import { COLLEAGUE_CALL_CATEGORY_CODE } from "@repo/api";
import { COLLEAGUE_CALL_CATEGORY_SYSTEM } from "@repo/fhir-systems/code-systems";
import { useCallback } from "react";
import { toast } from "sonner";
import { practitionerFhirIdFromProfile } from "@/shared/hooks/use-current-practitioner";
import { markCallAnswered } from "./call-answer-store";
import { closeCallRoom } from "./call-room";
import { soundForDelivery } from "./call-sounds";
import { deliveryForRing, ringFromNotification } from "./incoming-ring";
import { beginRing, endRing } from "./ring-store";

/**
 * Every colleague call in the clinic, and only colleague calls. The category
 * is the discriminator three aggregates on `Communication` depend on, so a
 * criteria without it would deliver the notification outbox here too.
 */
const CALL_CRITERIA = `Communication?category=${COLLEAGUE_CALL_CATEGORY_SYSTEM}|${COLLEAGUE_CALL_CATEGORY_CODE}`;

export function ColleagueCallListener() {
  const medplum = useMedplum();
  // The FHIR row id, not the domain identity: a call's parties are literal
  // `Practitioner/<row id>` references, and the domain id matches none of them.
  const meRef = practitionerFhirIdFromProfile(medplum.getProfile());

  const onNotification = useCallback(
    (bundle: Bundle) => {
      if (!meRef) return;
      const call = ringFromNotification(bundle);
      if (!call) return;

      const delivery = deliveryForRing(call, `Practitioner/${meRef}`, new Date());
      switch (delivery.kind) {
        case "ring":
          beginRing(delivery.call);
          break;
        case "withdraw":
          endRing(delivery.callId, "withdrawn");
          break;
        case "answered":
          // Answer arrives before the colleague joins the meeting. Change the
          // wait's wording now; the package expands when they are present.
          markCallAnswered(delivery.callId);
          break;
        case "unanswered":
          // Abandon is our own action, possibly from another tab: close here
          // too, but only a colleague's decline needs to tell us what happened.
          soundForDelivery(delivery);
          closeCallRoom(delivery.callId);
          if (delivery.status === "declined") toast.info("Your colleague declined the call");
          break;
        default:
          break;
      }
    },
    [meRef],
  );

  // An empty criteria is the documented way to subscribe to nothing; passing
  // `undefined` is what the data store does and is not the prescribed spelling.
  useSubscription(meRef ? CALL_CRITERIA : "", onNotification, {
    onError: (error) => console.error("[ColleagueCall] subscription error:", error),
  });

  return null;
}
